/**
 * Helpers puros de SEO multidioma (sin React ni DOM).
 *
 * - `SITE_ORIGIN`: origen público de producción, ÚNICA constante donde vive
 *   (sobrescribible con `REACT_APP_SITE_ORIGIN` para entornos de pruebas).
 * - `buildSeoLinks`: URLs absolutas por idioma (hreflang) y canonical de una ruta.
 * - `truncateDescription`: recorta un texto al tamaño de una meta descripción.
 *
 * Se separan del componente `SeoHead` para poder testearlos aislados y
 * reutilizarlos (p. ej. al generar un sitemap.xml en el futuro).
 */

import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from './languages';
import { getLanguageFromPath, localizePath, stripLanguagePrefix } from './paths';

/** Origen público de la web, sin barra final. */
export const SITE_ORIGIN = (
  process.env.REACT_APP_SITE_ORIGIN || 'https://www.intercasacanarias.com'
).replace(/\/+$/, '');

/** Valor de `og:locale` por idioma (formato `idioma_PAÍS` que exige Open Graph). */
export const OG_LOCALES = {
  es: 'es_ES',
  en: 'en_GB',
  fr: 'fr_FR',
  pt: 'pt_PT',
};

/** Longitud máxima recomendada de una meta descripción. */
export const MAX_DESCRIPTION_LENGTH = 155;

/**
 * Normaliza un pathname: sin query, hash ni barra final (salvo la raíz).
 * @param {string} pathname
 * @returns {string}
 */
const cleanPathname = (pathname) => {
  const bare = String(pathname || '/').split(/[?#]/)[0] || '/';
  return bare.length > 1 ? bare.replace(/\/+$/, '') || '/' : bare;
};

/**
 * Construye las URLs SEO de una ruta.
 *
 * @param {string} pathname - Ruta actual, con o sin prefijo (`/en/recetas`).
 * @returns {{
 *   language: string,
 *   canonical: string,
 *   alternates: Array<{hreflang: string, href: string}>
 * }} Idioma de la ruta, URL canónica (idioma actual) y alternates de los 4
 *   idiomas más `x-default` (español).
 */
export const buildSeoLinks = (pathname) => {
  const clean = cleanPathname(pathname);
  const language = getLanguageFromPath(clean);
  const barePath = stripLanguagePrefix(clean);
  const urlFor = (lang) => {
    const localized = localizePath(barePath, lang);
    return `${SITE_ORIGIN}${localized}`;
  };

  return {
    language,
    canonical: urlFor(language),
    alternates: [
      ...SUPPORTED_LANGUAGES.map((lang) => ({ hreflang: lang, href: urlFor(lang) })),
      { hreflang: 'x-default', href: urlFor(DEFAULT_LANGUAGE) },
    ],
  };
};

/**
 * Prepara un texto como meta descripción: quita etiquetas HTML, colapsa
 * espacios y, si supera el máximo, corta en el último límite de palabra y
 * añade «…».
 *
 * @param {string} [text] - Texto original (puede contener HTML).
 * @param {number} [max] - Longitud máxima incluida la elipsis.
 * @returns {string} Descripción lista para `<meta name="description">`.
 */
export const truncateDescription = (text, max = MAX_DESCRIPTION_LENGTH) => {
  const plain = String(text || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= max) return plain;
  const cut = plain.slice(0, max - 1);
  const lastSpace = cut.lastIndexOf(' ');
  const base = lastSpace > 0 ? cut.slice(0, lastSpace) : cut;
  return `${base.replace(/[\s,;:.\-–]+$/, '')}…`;
};
